import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const AquariumGlassThicknessCalculator = () => {
  const [aquariumData, setAquariumData] = useState({
    length: '',
    width: '',
    height: '',
    waterDepth: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAquariumData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateThickness = () => {
    const { length, width, height, waterDepth } = aquariumData;

    // Convert inputs to numbers
    const L = parseFloat(length) || 0;
    const W = parseFloat(width) || 0;
    const H = parseFloat(height) || 0;
    const D = parseFloat(waterDepth) || 0;

    if (L === 0 || W === 0 || H === 0 || D === 0) {
      setError('Please enter valid dimensions');
      return;
    }

    // Constants
    const waterDensity = 1000; // kg/m³
    const safetyFactor = 1.5;
    const glassStrength = 50; // MPa

    // Calculate hydrostatic pressure
    const pressure = waterDensity * 9.81 * D / 1000; // Convert D to meters

    // Calculate required thickness
    const thickness = (pressure * L * W * safetyFactor) / (2 * glassStrength * 1000); // Convert to mm

    const calculationSteps = [
      'Step 1: Calculate hydrostatic pressure',
      `Pressure = Density × Gravity × Depth = ${waterDensity} kg/m³ × 9.81 m/s² × ${D} mm = ${pressure.toFixed(2)} MPa`,
      '\nStep 2: Calculate required glass thickness',
      `Thickness = (Pressure × Length × Width × Safety Factor) / (2 × Glass Strength) = (${pressure.toFixed(2)} MPa × ${L} mm × ${W} mm × ${safetyFactor}) / (2 × ${glassStrength} MPa) = ${thickness.toFixed(2)} mm`
    ];

    const newResults = {
      dimensions: {
        length: L,
        width: W,
        height: H,
        waterDepth: D
      },
      requiredThickness: thickness.toFixed(2),
      calculationSteps
    };

    setResults(newResults);

    // Update chart data
    setChartData({
      labels: ['Length', 'Width', 'Height', 'Water Depth'],
      datasets: [
        {
          label: 'Dimensions (mm)',
          data: [L, W, H, D],
          backgroundColor: 'rgba(54, 162, 235, 0.5)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1
        }
      ]
    });
  };

  const exampleData = {
    length: 1200,
    width: 450,
    height: 500,
    waterDepth: 400,
    requiredThickness: 10
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">🛠️</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Aquarium Glass Thickness Calculator</h1>
            <p className="text-lg text-blue-600 font-semibold">Determine the required glass thickness for your aquarium</p>
          </div>
        </div>
      </div>

      <div className="calculator-description space-y-4 mb-12">
        <p className="text-gray-700">
          This calculator helps you determine the required glass thickness for your aquarium based on its dimensions and water depth. Ensure you enter accurate measurements for the best results.
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="length">
              Length (mm)
            </label>
            <input
              type="number"
              id="length"
              name="length"
              value={aquariumData.length}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="width">
              Width (mm)
            </label>
            <input
              type="number"
              id="width"
              name="width"
              value={aquariumData.width}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="height">
              Height (mm)
            </label>
            <input
              type="number"
              id="height"
              name="height"
              value={aquariumData.height}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="waterDepth">
              Water Depth (mm)
            </label>
            <input
              type="number"
              id="waterDepth"
              name="waterDepth"
              value={aquariumData.waterDepth}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          <button
            onClick={calculateThickness}
            className="w-full bg-green-600 text-white py-3 px-4 rounded-md text-sm font-semibold hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          >
            Calculate Thickness
          </button>

          {error && (
            <div className="text-red-500 text-center mt-4">
              {error}
            </div>
          )}
        </div>

        {results && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Results</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="text-xl font-semibold mb-4">Required Glass Thickness</h3>
                <p className="text-xl font-medium">{results.requiredThickness} mm</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-12">
              <div>
                <h3 className="text-xl font-semibold mb-4">Aquarium Dimensions</h3>
                <div className="h-96">
                  <Bar
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Dimensions (mm)'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Dimension'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Aquarium Dimensions'
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold mb-4">Calculation Steps</h3>
              <div className="space-y-4">
                {results.calculationSteps.map((step, index) => (
                  <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                    <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Example Calculation <span className="ml-2 text-2xl">📝</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Here's an example calculation using a sample aquarium:
          </p>
          
          <div className="bg-gray-50 p-6 rounded-lg">
            <h4 className="font-semibold mb-4">Sample Aquarium Data:</h4>
            <ul className="list-disc pl-8 space-y-2">
              <li>Length: {exampleData.length} mm</li>
              <li>Width: {exampleData.width} mm</li>
              <li>Height: {exampleData.height} mm</li>
              <li>Water Depth: {exampleData.waterDepth} mm</li>
            </ul>
            
            <h4 className="font-semibold mt-6 mb-4">Calculations:</h4>
            <ol className="list-decimal pl-8 space-y-4">
              <li>
                <p>Hydrostatic Pressure:</p>
                <ul className="list-none pl-8 space-y-2">
                  <li>Pressure = Density × Gravity × Depth = 1000 kg/m³ × 9.81 m/s² × 400 mm = 3.924 MPa</li>
                </ul>
              </li>
              <li>
                <p>Required Glass Thickness:</p>
                <ul className="list-none pl-8 space-y-2">
                  <li>Thickness = (Pressure × Length × Width × Safety Factor) / (2 × Glass Strength) = (3.924 MPa × 1200 mm × 450 mm × 1.5) / (2 × 50 MPa) = 10 mm</li>
                </ul>
              </li>
            </ol>
            
            <p className="mt-6 font-semibold">Interpretation:</p>
            <p>In this example, the required glass thickness for the aquarium is 10 mm.</p>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to use the Aquarium Glass Thickness Calculator:
          </p>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter Aquarium Dimensions</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Input the length of the aquarium in millimeters</li>
                <li>Input the width of the aquarium in millimeters</li>
                <li>Input the height of the aquarium in millimeters</li>
                <li>Input the water depth in millimeters</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Click Calculate</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>The calculator will determine the required glass thickness</li>
                <li>A visual representation of the dimensions will be displayed</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Interpret Results</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Review the required glass thickness for your aquarium</li>
                <li>Ensure the thickness meets safety standards</li>
              </ul>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Pro Tips</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Use millimeters for all dimensions</li>
                <li>Ensure all fields are filled with accurate measurements</li>
                <li>Consider consulting a professional for larger aquariums</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Understanding the Results <span className="ml-2 text-2xl">📊</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            The Aquarium Glass Thickness Calculator provides the required glass thickness based on the dimensions and water depth of your aquarium.
          </p>
          <ul className="list-none pl-8 space-y-4">
            <li className="flex items-center">
              <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
              <span className="font-medium">Required Thickness:</span> The minimum thickness of the glass needed to safely support the water pressure.
            </li>
          </ul>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Additional Tips <span className="ml-2 text-2xl">💡</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <ul className="list-none pl-8 space-y-4">
            <li className="flex items-center">
              <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
              <span className="font-medium">Data Quality:</span> Enter accurate dimensions to ensure the calculations are correct.
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
              <span className="font-medium">Safety Factor:</span> The calculator includes a safety factor to ensure the glass can handle additional stress.
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
              <span className="font-medium">Consult Professionals:</span> For larger or custom aquariums, consider consulting a professional.
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-red-600 rounded-full mr-2"></span>
              <span className="font-medium">Limitations:</span> The calculator assumes standard conditions and materials. Adjustments may be needed for special cases.
            </li>
          </ul>
          <p className="mt-6 italic text-gray-500">
            Note: Always ensure the glass thickness meets or exceeds the calculated value for safety.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AquariumGlassThicknessCalculator;